import { memo } from "react"
import { Box } from "@mui/material"
import { ArrowRight, Link } from "@mui/icons-material"
import PropTypes from "prop-types"
import classNames from "classnames"
import { isFunction } from "lodash"
import usePositionTooltip from "connected-components/dashboard/helpers/tooltips"
import OPTooltip from "components/material/tooltip"
import { TOOLTIP_ENTER_DELAY_MS } from "helpers/constants"

const LabelWithTooltip = memo(({ children, hasPositionTooltip, label, hasAction }) => {
  if (hasPositionTooltip) {
    return children
  }

  const title = hasAction ? `Click to open ${label} factor profile` : label
  return (
    <OPTooltip title={title} placement="bottom-start" enterDelay={TOOLTIP_ENTER_DELAY_MS}>
      {children}
    </OPTooltip>
  )
})

LabelWithTooltip.displayName = "LabelWithTooltip"

LabelWithTooltip.propTypes = {
  children: PropTypes.node.isRequired,
  hasPositionTooltip: PropTypes.bool.isRequired,
  label: PropTypes.string.isRequired,
  hasAction: PropTypes.bool,
}

const LabelCellContent = memo(({
  contributor,
  handleClickContributor,
  includeDrilldownArrows,
  canDrilldown,
}) => {
  const hasChildren = !!contributor.children
  const notHeld = Boolean(contributor.notHeld)
  const isUnknown = contributor.label === "Unknown"
  const hasAction = isFunction(contributor.action) && !notHeld && !hasChildren

  // Check if position tooltip has already been applied at the children level.
  const positionTooltip = usePositionTooltip(contributor)
  const hasPositionTooltip = contributor.tooltipValue || (positionTooltip && contributor.id !== "notheld")

  const executeClick = () => {
    if (isFunction(contributor.action)) {
      contributor.action(contributor)
    } else {
      handleClickContributor(contributor.id, hasChildren)
    }
  }

  const shouldDeferToRowDrilldown = canDrilldown && !hasAction && !hasChildren

  const handleCellClick = (event) => {
    if (shouldDeferToRowDrilldown) {
      return
    }
    if (hasAction || hasChildren) {
      event.stopPropagation()
    }
    executeClick()
  }

  const handleKeyDown = (event) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return
    }
    // Row-level drilldown handles keyboard activation for leaf rows.
    if (shouldDeferToRowDrilldown) {
      return
    }
    event.preventDefault()
    executeClick()
  }

  if (isUnknown) {
    return <span className="unknown">{contributor.label}</span>
  }

  if (notHeld) {
    return <span className="not-held">{contributor.label}</span>
  }

  return (
    <div
      className={classNames("table-item-link", {
        "has-action": hasAction,
        "no-children": !hasChildren,
        "can-drilldown": canDrilldown,
      })}
    >
      <Box
        component="span"
        role="button"
        tabIndex={0}
        onClick={handleCellClick}
        onKeyDown={handleKeyDown}
        sx={{
          display: "flex",
          alignItems: "center",
          flex: 1,
          minWidth: 0,
          textAlign: "left",
        }}
      >
        <LabelWithTooltip
          hasPositionTooltip={hasPositionTooltip}
          label={contributor.label}
          hasAction={hasAction}
        >
          <span className="table-item-label">{contributor.label}</span>
          {hasAction && <Link
            className="external-link-icon"
            sx={{
            fontSize: "small",
          }}
          />}
        </LabelWithTooltip>
        {includeDrilldownArrows && hasChildren && <ArrowRight />}
      </Box>
    </div>
  )
})

LabelCellContent.displayName = "LabelCellContent"

LabelCellContent.propTypes = {
  contributor: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    children: PropTypes.any,
    notHeld: PropTypes.bool,
    isNoLongerHeld: PropTypes.bool,
    action: PropTypes.func,
    tooltipValue: PropTypes.any,
  }).isRequired,
  handleClickContributor: PropTypes.func.isRequired,
  includeDrilldownArrows: PropTypes.bool,
  canDrilldown: PropTypes.bool,
}

export default LabelCellContent
